import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Package, 
  Mail, 
  Truck, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Users,
  Building,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  PieChart,
  Download
} from 'lucide-react'
import { deliveryApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const DeliveryStats = () => {
  const { selectedBuildingId, selectedBuildingName } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [dateRange, setDateRange] = useState({
    fromDate: '',
    toDate: ''
  })
  const [buildingStats, setBuildingStats] = useState(null)

  useEffect(() => {
    loadStats()
  }, [])

  useEffect(() => {
    if (selectedBuildingId) {
      loadBuildingStats(selectedBuildingId)
    } else {
      setBuildingStats(null)
    }
  }, [selectedBuildingId, dateRange])

  const loadStats = async () => {
    try {
      setLoading(true)
      const params = {
        ...(dateRange.fromDate && { fromDate: dateRange.fromDate }),
        ...(dateRange.toDate && { toDate: dateRange.toDate })
      }
      
      const response = await deliveryApi.getStats(params)
      console.log('Delivery stats:', response.data)
      setStats(response.data)
    } catch (error) {
      console.error('Error loading delivery stats:', error)
      setStats(null)
    } finally {
      setLoading(false)
    }
  }

  const loadBuildingStats = async (buildingId) => {
    try {
      const params = {
        ...(dateRange.fromDate && { fromDate: dateRange.fromDate }),
        ...(dateRange.toDate && { toDate: dateRange.toDate })
      }
      
      const response = await deliveryApi.getBuildingStats(buildingId, params)
      console.log('Building stats:', response.data)
      setBuildingStats(response.data)
    } catch (error) {
      console.error('Error loading building stats:', error)
      setBuildingStats(null)
    }
  }

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }))
  }

  const applyFilters = () => {
    loadStats()
    if (selectedBuildingId) {
      loadBuildingStats(selectedBuildingId)
    }
  }

  const clearFilters = () => {
    setDateRange({ fromDate: '', toDate: '' })
    setTimeout(() => {
      loadStats()
    }, 100)
  }

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0'
    return num.toLocaleString()
  }

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0%'
    return `${(value * 100).toFixed(1)}%`
  }

  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return '0 hours'
    if (hours < 24) return `${hours.toFixed(1)} hours`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return `${days}d ${remainingHours.toFixed(1)}h`
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 'Package': return <Package className="w-5 h-5" />
      case 'Mail': return <Mail className="w-5 h-5" />
      default: return <Truck className="w-5 h-5" />
    }
  }

  const getStatusIcon = (type) => {
    switch (type) {
      case 'delivered': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'collected': return <CheckCircle className="w-5 h-5 text-blue-600" />
      case 'overdue': return <AlertTriangle className="w-5 h-5 text-red-600" />
      case 'pending': return <Clock className="w-5 h-5 text-yellow-600" />
      default: return <XCircle className="w-5 h-5 text-gray-600" />
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <Link
              to="/deliveries"
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            Delivery Statistics
          </div>
        }
        action={
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateRange.fromDate}
                onChange={(e) => handleDateRangeChange('fromDate', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateRange.toDate}
                onChange={(e) => handleDateRangeChange('toDate', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
            >
              Apply Filters
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Main Statistics */}
      {stats && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Deliveries</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats.totalDeliveries)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pending</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats.pendingDeliveries)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Delivered</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats.deliveredCount)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Overdue</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatNumber(stats.overdueCount)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Collection Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Collected</span>
                  <span className="text-lg font-semibold text-blue-600">{formatNumber(stats.collectedCount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Collection Rate</span>
                  <span className="text-lg font-semibold text-blue-600">{formatPercentage(stats.collectionRate)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Avg Collection Time</span>
                  <span className="text-lg font-semibold text-blue-600">{formatHours(stats.averageCollectionTimeHours)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Rates</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Delivery Rate</span>
                  <span className="text-lg font-semibold text-green-600">{formatPercentage(stats.deliveryRate)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Today</span>
                  <span className="text-lg font-semibold text-green-600">{formatNumber(stats.todayDeliveries)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">This Week</span>
                  <span className="text-lg font-semibold text-green-600">{formatNumber(stats.weekDeliveries)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">This Month</span>
                  <span className="text-lg font-semibold text-green-600">{formatNumber(stats.monthDeliveries)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">AI Analytics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">AI Generated</span>
                  <span className="text-lg font-semibold text-purple-600">{formatNumber(stats.aiGeneratedCount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">AI Generation Rate</span>
                  <span className="text-lg font-semibold text-purple-600">{formatPercentage(stats.aiGenerationRate)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Types */}
          {stats.deliveryTypeCounts && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Types</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(stats.deliveryTypeCounts).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getDeliveryTypeIcon(type)}
                      <span className="ml-2 text-sm font-medium text-gray-700">{type}</span>
                    </div>
                    <span className="text-lg font-semibold text-gray-900">{formatNumber(count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Recipients */}
          {stats.topRecipients && stats.topRecipients.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Recipients</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Deliveries
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topRecipients.map((recipient, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {recipient.recipientName || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {recipient.recipientEmail || 'No email'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {recipient.unitNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(recipient.totalDeliveries)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(recipient.pendingDeliveries)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Senders */}
          {stats.topSenders && stats.topSenders.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Senders</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sender
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Deliveries
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Delivery Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topSenders.map((sender, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {sender.senderName || 'Unknown Sender'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(sender.totalDeliveries)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPercentage(sender.deliveryRate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Overdue Deliveries */}
          {stats.overdueDeliveries && stats.overdueDeliveries.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Overdue Deliveries</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Delivery
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Days Overdue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Delivered Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.overdueDeliveries.slice(0, 10).map((delivery, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/deliveries/${delivery.deliveryId}`}
                            className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                          >
                            #{delivery.deliveryId}
                          </Link>
                          <div className="text-sm text-gray-500">
                            {delivery.senderName || 'Unknown Sender'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {delivery.recipientName || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {delivery.unitNumber ? `Unit ${delivery.unitNumber}` : 'No unit'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            {delivery.daysOverdue} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {delivery.deliveredDate ? new Date(delivery.deliveredDate).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Building-specific stats */}
          {buildingStats && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-blue-900 mb-4">
                Building Statistics: {selectedBuildingName}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Total</div>
                  <div className="text-xl font-semibold text-gray-900">
                    {formatNumber(buildingStats.totalDeliveries)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Pending</div>
                  <div className="text-xl font-semibold text-yellow-600">
                    {formatNumber(buildingStats.pendingDeliveries)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Delivered</div>
                  <div className="text-xl font-semibold text-green-600">
                    {formatNumber(buildingStats.deliveredCount)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Collection Rate</div>
                  <div className="text-xl font-semibold text-blue-600">
                    {formatPercentage(buildingStats.collectionRate)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!stats && (
        <div className="text-center py-12">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No statistics available</h3>
          <p className="mt-1 text-sm text-gray-500">Unable to load delivery statistics at this time.</p>
        </div>
      )}
    </div>
  )
}

export default DeliveryStats